// Canonical sharing-UI template — "Delt med meg" list.
// Router-agnostic: pass LinkComponent (a wrapper over your router's Link) for SPA
// navigation to /shared/:shareId; defaults to a plain anchor.

import React, { useEffect, useState } from "react";
import type { SharingClient, SharedWithMeItem } from "./types";
import { DefaultLink, identityTypeLabel, type SharingLinkComponent, type TypeLabeller } from "./common";

export interface SharedWithMePanelProps {
  client: SharingClient;
  typeLabel?: TypeLabeller;
  LinkComponent?: SharingLinkComponent;
}

export const SharedWithMePanel: React.FC<SharedWithMePanelProps> = ({ client, typeLabel = identityTypeLabel, LinkComponent = DefaultLink }) => {
  const [items, setItems] = useState<SharedWithMeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setItems(await client.listSharedWithMe());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Kunne ikke hente delte beregninger.");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [client]);

  if (isLoading) return <div className="sm-placeholder"><span className="sm-spinner" /> Henter delte beregninger...</div>;
  if (error) {
    return (
      <div className="sm-error-box">
        <strong>Feil:</strong>
        <pre className="sm-error-details">{error}</pre>
      </div>
    );
  }
  if (items.length === 0) return <div className="sm-placeholder">Ingen beregninger er delt med deg ennå.</div>;

  return (
    <div className="sm-table-responsive">
      <table className="sm-table">
        <thead>
          <tr>
            <th>Tittel</th>
            <th>Type</th>
            <th>Delt av</th>
            <th>Delt</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.shareId}>
              <td title={item.subtitle ?? undefined}>{item.title}</td>
              <td>{typeLabel(item.resourceType)}</td>
              <td title={item.ownerEmail}>{item.ownerName || item.ownerEmail}</td>
              <td>{new Date(item.sharedAt).toLocaleDateString("nb-NO")}</td>
              <td style={{ textAlign: "right" }}>
                <LinkComponent href={`/shared/${encodeURIComponent(item.shareId)}`} className="sm-btn sm-btn-sm sm-btn-primary">Åpne</LinkComponent>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SharedWithMePanel;
